// SH-7 — Team manager role + Share permission (Specs/Launch OS Sharing — PRD (Nov 3, 2026).md, charter decision 7).
// The roles live in the team list on the server; this only shows them and asks the server to change them.
import { useState } from "react";

export type TeamRoles = { email: string; manager: boolean; share: boolean; owner: boolean };
type Saved = { email: string; manager: boolean; share: boolean };

export const roleLabel = (r?: Partial<TeamRoles>) =>
  r?.owner ? "Team manager · Owner" : r?.manager ? "Team manager" : "Team member";

// Remove shows only once the server's team list has loaded, and never on the owner
export const canRemove = (info: { team: { email: string; owner?: boolean }[] } | null, email: string) =>
  !!info && !info.team.find((m) => m.email.toLowerCase() === String(email || "").toLowerCase())?.owner;

export function TeamRoleControls({ name, roles, canManage, save, onSaved }: {
  name: string;
  roles: TeamRoles;
  canManage: boolean;
  save: (email: string, patch: { manager?: boolean; share?: boolean }) => Promise<Saved>;
  onSaved: (member: Saved) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = (patch: { manager?: boolean; share?: boolean }) => {
    setBusy(true);
    setError(null);
    save(roles.email, patch)
      .then(onSaved)
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't save. Try again."))
      .finally(() => setBusy(false));
  };
  const locked = !canManage || busy;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-gray-600" title={roles.manager ? "Team managers can always share" : undefined}>
          <input
            type="checkbox"
            aria-label={`${name} can share`}
            checked={roles.manager || roles.share}
            disabled={locked || roles.manager}
            onChange={(e) => change({ share: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          />
          Can share
        </label>
        <select
          aria-label={`Role for ${name}`}
          value={roles.manager ? "manager" : "member"}
          disabled={locked || roles.owner}
          onChange={(e) => change({ manager: e.target.value === "manager" })}
          className="rounded-lg border border-black/[0.08] bg-white py-1.5 pl-3 pr-8 text-xs font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <option value="manager">Team manager</option>
          <option value="member">Team member</option>
        </select>
      </div>
      {error && <p role="alert" className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
}
